import { z } from "zod";

/**
 * Runtime-validated view of `import.meta.env`. Misconfigured builds fail loudly at
 * startup rather than misbehaving later (app-design.md §9.4).
 *
 * S0/M0 surface is deliberately tiny: only the tile origin and the emulator flag.
 * Firebase vars (VITE_FB_*) join this schema in M1.
 */
const EnvSchema = z.object({
  VITE_TILE_BASE_URL: z
    .string()
    .min(1)
    .refine((v) => /^https?:\/\//.test(v), "VITE_TILE_BASE_URL must be an http(s) URL")
    .default("http://localhost:8788"),
  VITE_USE_EMULATORS: z
    .union([z.literal("true"), z.literal("false")])
    .default("false")
    .transform((v) => v === "true"),
  /**
   * Build-time product selector. `history` (DEFAULT) → the current public WW2 archive,
   * byte-for-byte unchanged. `osint` → boots the access-controlled OSINT console instead
   * (see `src/main.tsx`, which branches on this and lazy-loads the osint chunk). Set via
   * `.env.osint` / `vite build --mode osint`; the deploy agent owns the production wiring.
   */
  VITE_APP_MODE: z.union([z.literal("history"), z.literal("osint")]).default("history"),
});

export type Env = z.infer<typeof EnvSchema>;

/** The two products this codebase can build. */
export type AppMode = Env["VITE_APP_MODE"];

export const env: Env = EnvSchema.parse(import.meta.env);

/** Tile/manifest origin with any trailing slashes stripped, for safe path joins. */
export const TILE_BASE: string = env.VITE_TILE_BASE_URL.replace(/\/+$/, "");

/** Which product this build serves (`history` unless explicitly built with `VITE_APP_MODE=osint`). */
export const APP_MODE: AppMode = env.VITE_APP_MODE;

/** True only in an OSINT-console build; false for the default history app. */
export const isOsintMode: boolean = env.VITE_APP_MODE === "osint";
